import React, { useState } from "react";
import {
	Container,
	Grid,
	Typography,
	Box,
	Paper,
	CircularProgress,
	Button,
	Stack,
	Alert,
} from "@mui/material";
import { format } from "date-fns";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { buildProductKeysFromCsvRows } from "../pictorialUtils";

const UploadPage = ({ onBack, onApplyFilters, dateRange }) => {
	const [uploadedFile, setUploadedFile] = useState(null);
	const [uploadedData, setUploadedData] = useState([]);
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [fileInputKey, setFileInputKey] = useState(0);
	const [processingFailed, setProcessingFailed] = useState(false);
	const resolvedDateRange = dateRange || { startDate: new Date(), endDate: new Date() };
	const [showFilterPreview, setShowFilterPreview] = useState(false);
	const [filterPreviewData, setFilterPreviewData] = useState(null);

	// Build Key filter values from CSV rows (no Item Master lookup).
	const handleApplyToFilter = async () => {
		if (!uploadedData || uploadedData.length === 0) {
			setError("No CSV data available to apply as filters");
			return;
		}

		setIsUploading(true);
		setError("");
		setProcessingFailed(false);

		try {
			const keys = buildProductKeysFromCsvRows(uploadedData);
			if (keys.length === 0) {
				setError(
					"No valid product keys found. Ensure each row has brand, department, style, color, and pallete type."
				);
				setProcessingFailed(true);
				return;
			}

			const filters = {
				start_date: format(resolvedDateRange.startDate, "yyyy-MM-dd"),
				end_date: format(resolvedDateRange.endDate, "yyyy-MM-dd"),
				key: keys.join(","),
			};

			setFilterPreviewData({ filters, dateRange: resolvedDateRange, keys });
			setShowFilterPreview(true);
			setSuccess(`Filter preview generated with ${keys.length} product key(s). Please review and confirm.`);
			setFileInputKey((prevKey) => prevKey + 1);
		} catch (err) {
			console.error("Error generating filter preview:", err);
			setError("Failed to generate filter preview: " + err.message);
			setProcessingFailed(true);
			setSuccess("");
		} finally {
			setIsUploading(false);
		}
	};

	const handleRouteToMainPage = () => {
		if (!filterPreviewData) {
			setError("No filter data available");
			return;
		}

		try {
			if (onApplyFilters) {
				onApplyFilters(filterPreviewData.filters, filterPreviewData.dateRange);
			}
			setSuccess("Filters set successfully! Redirecting to main page...");
		} catch (err) {
			console.error("Error setting filters:", err);
			setError("Failed to set filters: " + err.message);
		}
	};

	const handleCancelFilterPreview = () => {
		setShowFilterPreview(false);
		setFilterPreviewData(null);
		setSuccess("");
		setProcessingFailed(false);
		setFileInputKey((prevKey) => prevKey + 1);
	};

	const handleFileUpload = (event) => {
		const file = event.target.files[0];
		if (file) {
			if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
				setError("Please upload a CSV file only.");
				return;
			}
			setUploadedFile(file);
			parseCSVFile(file);
		}
	};

	const parseCSVFile = (file) => {
		setIsUploading(true);
		setError("");
		setSuccess("");

		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const csvContent = e.target.result;
				const lines = csvContent.split("\n");

				if (lines.length < 2) {
					setError("CSV file must contain at least a header row and one data row.");
					setIsUploading(false);
					return;
				}

				const headers = lines[0].split(",").map((header) => header.trim().replace(/"/g, ""));

				const data = lines
					.slice(1)
					.filter((line) => line.trim())
					.map((line) => {
						const values = line.split(",").map((value) => value.trim().replace(/"/g, ""));
						const row = {};
						headers.forEach((header, index) => {
							row[header] = values[index] || "";
						});
						return row;
					});

				setUploadedData(data);
				setSuccess('CSV file uploaded successfully! Click "Apply to Filter" to continue.');
			} catch (err) {
				console.error("Error parsing CSV:", err);
				setError("Error parsing CSV file. Please check the file format.");
			} finally {
				setIsUploading(false);
			}
		};

		reader.onerror = () => {
			setError("Error reading file.");
			setIsUploading(false);
		};

		reader.readAsText(file);
	};

	const downloadSampleCSV = () => {
		try {
			const sampleData = [
				"brand,department,style,color,pallete type",
				"RARE RABBIT,T-SHIRT,REV,BLACK,PRIMARY",
				"RARE RABBIT,SHIRT,CLASSIC,BEIGE,DARK",
			];
			const csvContent = sampleData.join("\n");
			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", `sample-csv-structure-${format(new Date(), "yyyy-MM-dd")}.csv`);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			setSuccess("Sample CSV file downloaded successfully!");
		} catch (err) {
			console.error("Error generating sample CSV:", err);
			setError(`Failed to generate sample CSV: ${err.message}`);
		}
	};

	const clearUploadedData = () => {
		setUploadedFile(null);
		setUploadedData([]);
		setError("");
		setSuccess("");
		setShowFilterPreview(false);
		setFilterPreviewData(null);
		setFileInputKey((prevKey) => prevKey + 1);
		setProcessingFailed(false);
	};

	return (
		<Container maxWidth="lg" sx={{ pt: 3 }}>
			<Paper sx={{ p: 4, backgroundColor: "#fff" }}>
				<Box sx={{ mb: 4 }}>
					<Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }} variant="outlined">
						Back to Main Page
					</Button>
					<Typography variant="h4" sx={{ color: "#000", fontWeight: 600, mb: 1 }}>
						Sell Through Report Generator and Filter Uploader
					</Typography>
					<Typography variant="body1" sx={{ color: "#666" }}>
						Upload a CSV file to apply its rows as Key filters on the main page. Date range is set on the main page.
					</Typography>
				</Box>

				{error && (
					<Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
						{error}
					</Alert>
				)}
				{success && (
					<Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
						{success}
					</Alert>
				)}

				<Box sx={{ mb: 4, p: 3, border: "1px solid #e0e0e0", borderRadius: 2, backgroundColor: "#fafafa" }}>
					<Typography variant="h6" sx={{ mb: 3, color: "#000", fontWeight: 600 }}>
						Upload CSV File
					</Typography>

					<Box sx={{ mb: 3 }}>
						<Typography variant="subtitle1" sx={{ mb: 2, color: "#000", fontWeight: 500 }}>
							Upload CSV File
						</Typography>
						<Grid container spacing={3} alignItems="center" justifyContent="flex-start">
							<Grid item>
								<input
									accept=".csv"
									style={{ display: "none" }}
									id="csv-file-upload"
									type="file"
									onChange={handleFileUpload}
									disabled={isUploading}
									key={fileInputKey}
								/>
								<label htmlFor="csv-file-upload">
									<Button
										variant="outlined"
										component="span"
										disabled={isUploading}
										startIcon={<UploadFileIcon />}
										size="large"
										sx={{
											borderColor: "#000",
											color: "#000",
											"&:hover": {
												borderColor: "#333",
												backgroundColor: "rgba(0, 0, 0, 0.04)",
											},
										}}>
										{uploadedFile ? "Change File" : "Upload CSV File"}
									</Button>
								</label>
							</Grid>
							<Grid item>
								<Button
									variant="outlined"
									onClick={downloadSampleCSV}
									size="large"
									startIcon={<UploadFileIcon sx={{ transform: "rotate(180deg)" }} />}
									sx={{
										borderColor: "#007bff",
										color: "#007bff",
										"&:hover": {
											borderColor: "#0056b3",
											backgroundColor: "rgba(0, 123, 255, 0.04)",
										},
									}}>
									Download Sample CSV
								</Button>
							</Grid>
						</Grid>
					</Box>

					<Box sx={{ mb: 3 }}>
						<Grid container spacing={2} alignItems="center" justifyContent="flex-start">
							<Grid item>
								{!showFilterPreview ? (
									<Button
										variant="contained"
										onClick={handleApplyToFilter}
										disabled={uploadedData.length === 0}
										size="large"
										sx={{
											backgroundColor: "#007bff",
											"&:hover": { backgroundColor: "#0056b3" },
											"&:disabled": { backgroundColor: "#6c757d" },
										}}>
										Apply to Filter
									</Button>
								) : (
									<Button
										variant="contained"
										onClick={handleRouteToMainPage}
										size="large"
										sx={{
											backgroundColor: "#28a745",
											"&:hover": { backgroundColor: "#218838" },
										}}>
										Set Filters & Go to Main Page
									</Button>
								)}
							</Grid>
							<Grid item>
								<Stack direction="row" spacing={2} justifyContent="flex-end">
									{showFilterPreview && (
										<Button
											variant="outlined"
											onClick={handleCancelFilterPreview}
											size="large"
											sx={{
												borderColor: "#6c757d",
												color: "#6c757d",
												"&:hover": {
													borderColor: "#545b62",
													backgroundColor: "rgba(108, 117, 125, 0.04)",
												},
											}}>
											Cancel
										</Button>
									)}
									{processingFailed && !showFilterPreview && (
										<Button
											variant="outlined"
											onClick={clearUploadedData}
											size="large"
											sx={{
												borderColor: "#dc3545",
												color: "#dc3545",
												"&:hover": {
													borderColor: "#c82333",
													backgroundColor: "rgba(220, 53, 69, 0.04)",
												},
											}}>
											Reset & Try Again
										</Button>
									)}
									{uploadedData.length > 0 && !showFilterPreview && !processingFailed && (
										<Button
											variant="outlined"
											onClick={clearUploadedData}
											size="large"
											sx={{
												borderColor: "#dc3545",
												color: "#dc3545",
												"&:hover": {
													borderColor: "#c82333",
													backgroundColor: "rgba(220, 53, 69, 0.04)",
												},
											}}>
											Clear Data
										</Button>
									)}
								</Stack>
							</Grid>
						</Grid>
					</Box>

					{showFilterPreview && filterPreviewData && (
						<Box sx={{ mb: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1, backgroundColor: "#f8f9fa" }}>
							<Typography variant="subtitle2" sx={{ mb: 1, color: "#000", fontWeight: 600 }}>
								Filter Preview (will be set on main page):
							</Typography>
							<Typography variant="body2" sx={{ color: "#666", fontWeight: 500, mb: 1 }}>
								Key ({filterPreviewData.keys.length}):
							</Typography>
							<Box sx={{ maxHeight: 160, overflow: "auto" }}>
								{filterPreviewData.keys.map((key) => (
									<Typography
										key={key}
										variant="body2"
										sx={{ color: "#007bff", fontFamily: "monospace", fontSize: "0.8rem", mb: 0.5 }}>
										{key}
									</Typography>
								))}
							</Box>
							<Typography variant="body2" sx={{ mt: 2, color: "#666", fontStyle: "italic" }}>
								Date Range: {format(resolvedDateRange.startDate, "dd-MM-yyyy")} -{" "}
								{format(resolvedDateRange.endDate, "dd-MM-yyyy")}
							</Typography>
							<Typography variant="body2" sx={{ mt: 1, color: "#666", fontStyle: "italic" }}>
								Note: Keys will be pre-selected on the main page. You can modify them before applying.
							</Typography>
						</Box>
					)}

					{isUploading && (
						<Box
							sx={{
								mt: 3,
								display: "flex",
								alignItems: "center",
								gap: 2,
								p: 3,
								backgroundColor: "#e3f2fd",
								borderRadius: 1,
							}}>
							<CircularProgress size={24} />
							<Typography variant="body1" sx={{ color: "#1976d2", fontWeight: 500 }}>
								Processing uploaded data... Please wait.
							</Typography>
						</Box>
					)}
				</Box>

				<Box sx={{ p: 3, border: "1px solid #e0e0e0", borderRadius: 2, backgroundColor: "#f8f9fa", mb: 3 }}>
					<Typography variant="h6" sx={{ mb: 2, color: "#000", fontWeight: 600 }}>
						Instructions
					</Typography>
					<Stack spacing={1}>
						<Typography variant="body2" sx={{ color: "#666" }}>
							Date range is selected on the main page.
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							1. Upload a CSV with columns: <b>brand, department, style, color, pallete type</b>
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							2. Each row is converted to a Key: <b>brand-department-style-color-pallete type</b>
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							3. Click <b>"Apply to Filter"</b> to preview the Keys, then <b>"Set Filters & Go to Main Page"</b> to
							apply them.
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 2, fontSize: "0.9em" }}>
							- No Item Master lookup — keys are built directly from your CSV rows
						</Typography>
						<Typography variant="body2" sx={{ color: "#666", ml: 2, fontSize: "0.9em" }}>
							- On the main page you can modify the Key filter and click "Apply Filters" when ready
						</Typography>
						<Typography variant="body2" sx={{ color: "#666" }}>
							Example header:{" "}
							<span style={{ fontFamily: "monospace", color: "#2e7d32" }}>
								brand,department,style,color,pallete type
							</span>
						</Typography>
					</Stack>
				</Box>
			</Paper>
		</Container>
	);
};

export default UploadPage;
